import React from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { DailyHoursData } from '@/services/reportService'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
)

interface DailyHoursChartProps {
  data: DailyHoursData[]
  loading: boolean
}

const DailyHoursChart: React.FC<DailyHoursChartProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="card p-6 mb-6">
        <h3 className="text-xl font-semibold text-secondary-800 mb-4">每日工時統計</h3>
        <div className="h-80 bg-secondary-100 rounded animate-pulse flex items-center justify-center">
          <div className="text-secondary-500">載入中...</div>
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="card p-6 mb-6">
        <h3 className="text-xl font-semibold text-secondary-800 mb-4">每日工時統計</h3>
        <div className="h-80 flex items-center justify-center text-secondary-500">
          <div className="text-center">
            <div className="text-4xl mb-2">📊</div>
            <p>暫無工時數據</p>
          </div>
        </div>
      </div>
    )
  }

  const chartData = {
    labels: data.map(item => {
      // 避免時區轉換問題，直接解析 YYYY-MM-DD 格式
      const dateParts = item.date.split('-')
      if (dateParts.length === 3) {
        const month = parseInt(dateParts[1])
        const day = parseInt(dateParts[2])
        return `${month}/${day}`
      }
      // 降級處理
      const date = new Date(item.date + 'T00:00:00')
      return `${date.getMonth() + 1}/${date.getDate()}`
    }),
    datasets: [
      {
        label: '工時 (小時)',
        data: data.map(item => item.hours),
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 1,
        borderRadius: 4,
        borderSkipped: false,
      },
    ],
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const hours = context.parsed.y
            const wholeHours = Math.floor(hours)
            const minutes = Math.round((hours - wholeHours) * 60)
            
            if (minutes === 0) {
              return `工時: ${wholeHours} 小時`
            }
            return `工時: ${wholeHours} 小時 ${minutes} 分鐘`
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          callback: function(value: any) {
            return `${value}h`
          }
        }
      },
    },
  }

  const totalHours = data.reduce((sum, item) => sum + item.hours, 0)
  const averageHours = data.length > 0 ? totalHours / data.length : 0
  const maxHours = Math.max(...data.map(item => item.hours))
  const workingDays = data.filter(item => item.hours > 0).length

  return (
    <div className="card p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold text-secondary-800">每日工時統計</h3>
        <div className="flex space-x-4 text-sm text-secondary-600">
          <span>總工時: {totalHours.toFixed(1)}h</span>
          <span>平均: {averageHours.toFixed(1)}h</span>
          <span>最高: {maxHours.toFixed(1)}h</span>
          <span>工作天數: {workingDays}天</span>
        </div>
      </div>
      
      <div className="h-80">
        <Bar data={chartData} options={options} />
      </div>
      
      {/* 工時分析 */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
        <div className="p-3 bg-blue-50 rounded-lg">
          <div className="text-lg font-semibold text-blue-600">{totalHours.toFixed(1)}h</div>
          <div className="text-sm text-blue-500">總工時</div>
        </div>
        <div className="p-3 bg-green-50 rounded-lg">
          <div className="text-lg font-semibold text-green-600">{averageHours.toFixed(1)}h</div>
          <div className="text-sm text-green-500">日均工時</div>
        </div>
        <div className="p-3 bg-purple-50 rounded-lg">
          <div className="text-lg font-semibold text-purple-600">{maxHours.toFixed(1)}h</div>
          <div className="text-sm text-purple-500">單日最高</div>
        </div>
        <div className="p-3 bg-orange-50 rounded-lg">
          <div className="text-lg font-semibold text-orange-600">{workingDays}</div>
          <div className="text-sm text-orange-500">工作天數</div>
        </div>
      </div>
    </div>
  )
}

export default DailyHoursChart
